import {
  type BillingRun,
  type ReferralSummaryResponse,
  ReferralSummaryResponse as ReferralSummaryResponseSchema,
} from '@uit/shared/schemas/billing';
import { useState } from 'react';
import { useApi } from '../../lib/api.js';
import s from './RunReportsPanel.module.scss';

// Reports for a stored billing run (T-111): re-download the output CSV and pull
// per-referrer referral reports — the run's rows filtered to engagements
// CURRENTLY referred by that vendor (so a T-110 backfill done after the run
// still counts). Mockup: docs/mockups/referral_reports_mockup.html (rev 2).

async function downloadCsv(url: string, fallbackName: string): Promise<void> {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error(`${r.status}`);
  const disposition = r.headers.get('content-disposition');
  const filename = /filename="([^"]+)"/.exec(disposition ?? '')?.[1] ?? fallbackName;
  const blob = await r.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export function RunReportsPanel({ run }: { run: BillingRun }) {
  const { data: summary, error } = useApi<ReferralSummaryResponse>(
    `/api/billing/runs/${run.id}/referral-summary`,
    ReferralSummaryResponseSchema,
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function download(url: string, fallbackName: string): Promise<void> {
    setDownloadError(null);
    try {
      await downloadCsv(url, fallbackName);
    } catch (err) {
      setDownloadError(`Download failed (${err instanceof Error ? err.message : String(err)}).`);
    }
  }

  // Guard against a stale summary while switching runs (useApi keeps the
  // previous run's data until the new fetch lands).
  const current = summary && summary.run_id === run.id ? summary : null;

  return (
    <section className={s.section}>
      <div className={s.head}>
        <h2 className={s.sectionTitle}>Run {run.period} — reports</h2>
        <button
          type="button"
          className={s.secondaryBtn}
          onClick={() =>
            void download(
              `/api/billing/runs/${run.id}/output.csv`,
              `${run.period}_Updated_Processed_File_Format.csv`,
            )
          }
        >
          Re-download output CSV
        </button>
      </div>
      <p className={s.sectionSub}>
        Referral reports: this run&rsquo;s output filtered per referring vendor — same columns, no
        dollar calculation (each vendor computes their own fee and bills uIT back). Attribution is
        read now, not at run time, so referrers backfilled after the run still show up. Fewer names
        than a vendor&rsquo;s referred total is expected when referred people had no hours this
        period.
      </p>

      {error && <div className={s.error}>Failed to load referral summary: {error.message}</div>}
      {downloadError && (
        <div className={s.error} role="alert">
          {downloadError}
        </div>
      )}

      {current && current.vendors.length === 0 && (
        <div className={s.empty}>
          No referrer vendors are flagged yet — manage the referrer list in Settings.
        </div>
      )}

      {current && current.vendors.length > 0 && (
        <table className={s.table}>
          <thead>
            <tr>
              <th scope="col">Referrer vendor</th>
              <th scope="col">Referred rows in {run.period}</th>
              <th scope="col" style={{ textAlign: 'right' }}>
                Report
              </th>
            </tr>
          </thead>
          <tbody>
            {current.vendors.map((v) => (
              <tr key={v.id}>
                <td>
                  <span className={s.vname}>{v.business_name}</span>
                  <div className={s.vmeta}>
                    {v.total_referred_engagements} referred engagement
                    {v.total_referred_engagements === 1 ? '' : 's'} total
                  </div>
                </td>
                <td>
                  {v.rows > 0 ? (
                    <span className={s.countPill}>
                      {v.rows} row{v.rows === 1 ? '' : 's'} · {v.workers} worker
                      {v.workers === 1 ? '' : 's'}
                    </span>
                  ) : (
                    <>
                      <span className={`${s.countPill} ${s.countPillZero}`}>0 rows</span>
                      <div className={s.vmeta}>no referred engagements billed in {run.period}</div>
                    </>
                  )}
                </td>
                <td className={s.cellRight}>
                  <button
                    type="button"
                    className={s.downloadBtn}
                    disabled={v.rows === 0}
                    onClick={() =>
                      void download(
                        `/api/billing/runs/${run.id}/referral-report?vendor_id=${v.id}`,
                        `${run.period}_${v.business_name}_Referral_Report.csv`,
                      )
                    }
                  >
                    Download CSV
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
